import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { defaultApplicationSteps, type FormStep } from '../../content/applicationForm';
import { defaultSiteContent } from '../../content/defaultSiteContent';
import type { SiteContent } from '../../content/types';
import { adminApi } from '../../lib/adminApi';
import type { Litter } from '../../lib/api';

const fallbackLitters: Litter[] = [
  { id: 'vixen-apollo', title: 'Vixen × Apollo', description: 'Classic structure, steady family temperaments, and full health clearances.', expectedDate: '2026-09-15', spotsLeft: 3, status: 'available', imageUrl: '/images/bento_puppies.jpg' },
  { id: 'fame-tba', title: 'Fame × TBA', description: 'Fame’s next litter is planned for spring.', expectedDate: '2027-03-15', spotsLeft: 0, status: 'planned', imageUrl: '/images/about.jpg' },
];

type CmsSnapshot = {
  content: SiteContent;
  litters: Litter[];
  steps: FormStep[];
};

const defaultSnapshot = (): CmsSnapshot => ({
  content: defaultSiteContent,
  litters: fallbackLitters,
  steps: defaultApplicationSteps,
});

const snapshotsMatch = (left: CmsSnapshot, right: CmsSnapshot) => JSON.stringify(left) === JSON.stringify(right);

type CmsValue = {
  content: SiteContent;
  litters: Litter[];
  steps: FormStep[];
  dirty: boolean;
  saving: boolean;
  loaded: boolean;
  setContent: (updater: (current: SiteContent) => SiteContent) => void;
  setLitters: (updater: (current: Litter[]) => Litter[]) => void;
  setSteps: (updater: (current: FormStep[]) => FormStep[]) => void;
  save: () => Promise<boolean>;
  discardChanges: () => void;
  resetContent: () => void;
  resetLitters: () => void;
  resetSteps: () => void;
};

const CmsContext = createContext<CmsValue | null>(null);

export function CmsProvider({ children }: { children: ReactNode }) {
  const [content, setContentState] = useState<SiteContent>(defaultSiteContent);
  const [litters, setLittersState] = useState<Litter[]>(fallbackLitters);
  const [steps, setStepsState] = useState<FormStep[]>(defaultApplicationSteps);
  const [baseline, setBaseline] = useState<CmsSnapshot>(defaultSnapshot);
  const [saving, setSaving] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const savingRef = useRef(false);

  useEffect(() => {
    let active = true;
    adminApi.getState().then((state) => {
      if (!active) return;
      const saved: CmsSnapshot = {
        content: state.content ?? defaultSiteContent,
        litters: state.litters ?? fallbackLitters,
        steps: state.formConfig?.steps ?? defaultApplicationSteps,
      };
      setContentState(saved.content);
      setLittersState(saved.litters);
      setStepsState(saved.steps);
      setBaseline(saved);
      setLoaded(true);
    }).catch(() => undefined);
    return () => { active = false; };
  }, []);

  const setContent = useCallback((updater: (current: SiteContent) => SiteContent) => {
    if (!loaded) return;
    setContentState(updater);
  }, [loaded]);

  const setLitters = useCallback((updater: (current: Litter[]) => Litter[]) => {
    if (!loaded) return;
    setLittersState(updater);
  }, [loaded]);

  const setSteps = useCallback((updater: (current: FormStep[]) => FormStep[]) => {
    if (!loaded) return;
    setStepsState(updater);
  }, [loaded]);

  const current = useMemo<CmsSnapshot>(() => ({ content, litters, steps }), [content, litters, steps]);
  const dirty = loaded && !snapshotsMatch(current, baseline);

  useEffect(() => {
    if (!dirty) return;
    const warnBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', warnBeforeUnload);
    return () => window.removeEventListener('beforeunload', warnBeforeUnload);
  }, [dirty]);

  const save = useCallback(async () => {
    if (!loaded || savingRef.current) return false;
    const pending = { content, litters, steps };
    savingRef.current = true;
    setSaving(true);
    try {
      await Promise.all([adminApi.saveContent(pending.content), adminApi.saveLitters(pending.litters), adminApi.saveFormConfig(pending.steps)]);
      setBaseline(pending);
      return true;
    } catch {
      return false;
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
  }, [content, litters, loaded, steps]);

  const discardChanges = useCallback(() => {
    if (!loaded || savingRef.current) return;
    setContentState(baseline.content);
    setLittersState(baseline.litters);
    setStepsState(baseline.steps);
  }, [baseline, loaded]);

  const resetContent = useCallback(() => {
    if (loaded) setContentState(defaultSiteContent);
  }, [loaded]);

  const resetLitters = useCallback(() => {
    if (loaded) setLittersState(fallbackLitters);
  }, [loaded]);

  const resetSteps = useCallback(() => {
    if (loaded) setStepsState(defaultApplicationSteps);
  }, [loaded]);

  const value: CmsValue = {
    content, litters, steps, dirty, saving, loaded,
    setContent, setLitters, setSteps, save, discardChanges,
    resetContent, resetLitters, resetSteps,
  };

  return <CmsContext.Provider value={value}>{children}</CmsContext.Provider>;
}

export function useCms(): CmsValue {
  const value = useContext(CmsContext);
  if (!value) throw new Error('useCms must be used within CmsProvider');
  return value;
}
